import { z } from "zod";

import { DbEnum } from "../../../../generated/prisma-enums";
import { isPostgresStorableText } from "../../../../shared/core/postgres-storable-text";
import { storableText, storableUrl } from "../../../../shared/core/storable-text.schema";
import {
  DOCUMENT_METADATA_KEY_PATTERN,
  DOCUMENT_METADATA_KEY_RULE
} from "../../../../shared/domain/documents/metadata-key-rule";
import {
  DOCUMENT_METADATA_BUDGET_BYTES,
  DOCUMENT_METADATA_SIZE_RULE,
  metadataByteSize
} from "../../../../shared/domain/documents/metadata-size-rule";
import { DOCUMENT_METADATA_VALUE_TEXT_RULE } from "../../../../shared/domain/documents/metadata-value-text-rule";
import { optionalQueryParam, PaginationParamsSchema } from "./common.schemas";

// ============================================================================
// DOCUMENT ENUMS
// ============================================================================

/**
 * Document processing status. Derived from the generated `DocumentStatus`
 * values so the filter cannot drift from the Prisma enum it is matched against.
 *
 * `READY` — not `COMPLETED`, not `PROCESSED` — is terminal success. Both of
 * those wrong literals shipped in our own docs and example scripts, and neither
 * could ever be true, so every "poll until success" loop built on them ran to
 * timeout (NEX-3087).
 */
export const DocumentStatusSchema = z.enum(DbEnum.DOCUMENT_STATUS_VALUES);

/** Document type. Derived from the generated `DocumentType` values. */
export const DocumentTypeSchema = z.enum(DbEnum.DOCUMENT_TYPE_VALUES);

// ============================================================================
// PATH PARAMS
// ============================================================================

// `Document.id` is `@db.Uuid`, so a non-uuid can only reach Postgres as a failed
// cast. Checking it here answers 400 instead of 500.
export const DocumentIdParamSchema = z.object({
  documentId: z.string().uuid()
});

// ============================================================================
// DOCUMENT RESPONSE
// ============================================================================

export const DocumentSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.string(),
  status: z.string(),
  createdAt: z.string()
});

export const DocumentDetailSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  type: z.string(),
  sourceType: z.string().nullable(),
  mimeType: z.string().nullable(),
  status: z.string(),
  isFolder: z.boolean(),
  sourceUrl: z.string().nullable(),
  size: z.number().nullable(),
  errorMessage: z.string().nullish(),
  totalChildren: z.number(),
  readyChildren: z.number(),
  processingChildren: z.number(),
  pendingChildren: z.number(),
  errorChildren: z.number(),
  processingProgress: z.number(),
  tags: z.array(z.string()),
  /** User-declared filterable attributes as stored (raw, pre-index). */
  metadata: z.record(z.string(), z.unknown()).nullable(),
  createdAt: z.string(),
  updatedAt: z.string().nullable()
});

export const GoogleSheetResultSchema = z.object({
  folder: DocumentSchema,
  sheets: z.array(DocumentSchema),
  message: z.string()
});

// ============================================================================
// DOCUMENT CREATION BODIES
// ============================================================================

export { DOCUMENT_METADATA_KEY_PATTERN, DOCUMENT_METADATA_KEY_RULE };

const DocumentMetadataKeySchema = z
  .string()
  .regex(DOCUMENT_METADATA_KEY_PATTERN, DOCUMENT_METADATA_KEY_RULE);

/**
 * Zod reports a refused record key as "Invalid key in record" and nests the key
 * schema's message one level down, where the 400 body never reads it. This
 * puts the rule on the issue the 400 prints, at the path of the refused key.
 * Every other issue keeps Zod's own message.
 */
const metadataKeyRuleErrorMap: z.core.$ZodErrorMap = (issue) =>
  issue.code === "invalid_key" ? DOCUMENT_METADATA_KEY_RULE : undefined;

/**
 * User-declared, filterable document attributes (e.g. `{ language: "fr" }`).
 * Scalars and string arrays are accepted; the backend coerces every value to a
 * string / string[] and strips reserved keys before indexing.
 */
export const DocumentMetadataInputSchema = z
  .record(
    DocumentMetadataKeySchema,
    z.union([
      z.string(),
      z.number(),
      z.boolean(),
      z.array(z.union([z.string(), z.number(), z.boolean()]))
    ]),
    { error: metadataKeyRuleErrorMap }
  )
  // The budget every indexed record must fit, measured as the index measures
  // it; refused here with our own words so no patch reaches the index only to
  // come back as a vendor 400. A refinement, not a wrapper: the CLI's contract
  // descriptors read this schema's shape and must keep seeing a record.
  .superRefine((metadata, ctx) => {
    // A value Postgres cannot store fails the WHOLE document write as a 500, so it
    // is refused here, at the key (and index) that carries it.
    for (const [key, value] of Object.entries(metadata)) {
      const items = Array.isArray(value) ? value : [value];
      items.forEach((item, index) => {
        if (typeof item !== "string" || isPostgresStorableText(item)) return;
        ctx.addIssue({
          code: "custom",
          path: Array.isArray(value) ? [key, index] : [key],
          message: DOCUMENT_METADATA_VALUE_TEXT_RULE
        });
      });
    }
    const bytes = metadataByteSize(metadata);
    if (bytes <= DOCUMENT_METADATA_BUDGET_BYTES) return;
    ctx.addIssue({
      code: "custom",
      message: `metadata serializes to ${bytes} bytes; ${DOCUMENT_METADATA_SIZE_RULE}`
    });
  });

export type DocumentMetadataInput = z.infer<typeof DocumentMetadataInputSchema>;

/**
 * Metadata constraint for filtering retrieval (e.g. `{ language: "fr" }`).
 * Narrower than the write schema: values are `string | string[]` only, matching
 * how metadata is stored in the retrieval index (scalar → `$eq`, array → `$in`).
 * Reuses the write rule so a bad key 400s instead of silently matching nothing.
 */
export const DocumentMetadataFilterSchema = z.record(
  DocumentMetadataKeySchema,
  z.union([z.string(), z.array(z.string())]),
  { error: metadataKeyRuleErrorMap }
);

export type DocumentMetadataFilter = z.infer<typeof DocumentMetadataFilterSchema>;

export const CreateTextDocumentBodySchema = z.object({
  name: z.string().trim().min(1, "Name is required"),
  // Refined, not `.trim()`: the check rejects whitespace-only text, which indexes
  // to nothing, while the stored content keeps its leading indentation.
  content: z
    .string()
    .refine((value) => value.trim().length > 0, "Content must contain text, not only whitespace"),
  description: z.string().optional(),
  metadata: DocumentMetadataInputSchema.optional()
});

export const AddWebsiteDocumentBodySchema = z.object({
  url: storableUrl(z.string().url("url must be a valid URL")),
  mode: z.enum(["sitemap", "crawl"]),
  config: z
    .object({
      urls: z.array(storableUrl(z.string())).optional(),
      max_depth: z.number().int().min(1).optional(),
      max_pages: z.number().int().min(1).optional()
    })
    .optional(),
  description: z.string().optional(),
  metadata: DocumentMetadataInputSchema.optional()
});

export const CreateGoogleSheetDocumentBodySchema = z.object({
  name: z.string().trim().min(1, "Name is required"),
  url: z.string().url("url must be a valid URL"),
  metadata: z
    .object({
      hasHeaderRow: z.boolean().optional()
    })
    .optional()
});

// ============================================================================
// COLLECTION LINKING
// ============================================================================

export const AttachCollectionDocumentsBodySchema = z.object({
  documentIds: z.array(z.string()).min(1, "At least one document ID is required")
});

export const AttachCollectionDocumentsResponseSchema = z.object({
  message: z.string()
});

// ============================================================================
// DOCUMENT LIST (enhanced)
// ============================================================================

export const DocumentSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  displayName: z.string().nullable().optional(),
  type: z.string(),
  sourceType: z.string().nullable(),
  status: z.string(),
  isFolder: z.boolean(),
  parentId: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date().nullable()
});

export const ListDocumentsParamsSchema = PaginationParamsSchema.extend({
  // Enum-checked here rather than left to Prisma. Both columns are Postgres
  // enums, so an out-of-enum value used to reach the query and come back as a
  // `PrismaClientValidationError` — rendered as `Invalid request data` with an
  // EMPTY path, naming neither the field nor the allowed values. Checking here
  // answers the way `/executions` does: the field, and the complete allowed set,
  // in one call (NEX-3087).
  //
  // Wrapped in `optionalQueryParam` for the same reason the uuid filters below
  // are, and it matters MORE here: tightening a filter that used to be
  // `z.string()` would otherwise turn `?status=` — the cleared dropdown, which
  // `qs` delivers as `""` — from an unfiltered 200 into a 400 naming an allowed
  // set that does not include "". The list repository reads a filter with
  // `if (filters?.status)`, so `""` has always meant "no filter"; the enum check
  // has to agree with that or this fix ships a regression of its own.
  type: optionalQueryParam(DocumentTypeSchema),
  status: optionalQueryParam(DocumentStatusSchema),
  // Both filter `@db.Uuid` columns — `parentId` matches the uuid-checked field on
  // `CreateDocumentFolderBodySchema` below, `collectionId` matches `Collection.id`.
  //
  // `optionalQueryParam`, not a bare `.optional()`: this schema is parsed from the
  // QUERY STRING, where `qs` turns `?parentId=` into `""` rather than dropping the
  // key. A bare `.optional()` sends that `""` into the uuid check and 400s a
  // request that means "no filter". On a path param or a body, `""` really is a
  // caller error and the bare form is right — the distinction is the parse source.
  parentId: optionalQueryParam(z.string().uuid()),
  collectionId: optionalQueryParam(z.string().uuid()),
  search: z.string().optional(),
  // Routed through `optionalQueryParam` for the same two reasons its siblings
  // are, and it is the last field on this schema that needed it: hand-rolled as
  // a bare `z.preprocess(...)`, it 400s `?isFolder=` (the empty value never
  // becomes `undefined`, so `z.boolean()` sees `""`) AND it is emitted as a
  // REQUIRED property, which is what kept `documents_list` advertising `query`
  // as a mandatory MCP input even after the filters beside it were fixed. The
  // inner preprocess still owns the query-string spelling of a boolean; the
  // repository reads the result with `if (filters?.isFolder !== undefined)`, so
  // absence has always meant "no filter".
  isFolder: optionalQueryParam(
    z.preprocess((v) => (v === "true" ? true : v === "false" ? false : v), z.boolean())
  )
});

// ============================================================================
// DOCUMENT UPDATE
// ============================================================================

export const UpdateDocumentBodySchema = z.object({
  name: storableText(z.string().trim().min(1)).optional(),
  description: storableText(z.string()).optional(),
  tags: z.array(z.string()).optional(),
  metadata: DocumentMetadataInputSchema.optional()
});

// ============================================================================
// DOCUMENT FOLDER
// ============================================================================

export const CreateDocumentFolderBodySchema = z.object({
  name: z.string().trim().min(1),
  parentId: z.string().uuid().optional()
});

// ============================================================================
// DOCUMENT DOWNLOAD
// ============================================================================

export const DownloadDocumentResponseSchema = z.object({
  url: z.string(),
  fileName: z.string(),
  mimeType: z.string().nullable(),
  expiresIn: z.number()
});

// ============================================================================
// DOCUMENT PREVIEW
// ============================================================================

export const PreviewDocumentResponseSchema = z.object({
  url: z.string(),
  fileName: z.string(),
  mimeType: z.string().nullable(),
  expiresIn: z.number()
});

// ============================================================================
// DOCUMENT REPROCESS
// ============================================================================

export const ReprocessDocumentResponseSchema = z.object({
  status: z.string(),
  message: z.string()
});

/**
 * The non-file form fields of `POST /public/v1/documents/file`.
 *
 * Multipart is the one place where the contract's `Body` and the handler's
 * `@Body()` describe a request that also carries a file — `multipart` names the
 * file field, this names the text fields beside it. Before both existed the
 * descriptor declared neither, so a generic consumer saw a route with no body
 * at all and could not have sent either field.
 *
 * **`metadata` is a STRING here, not `DocumentMetadataInputSchema`.** A
 * multipart text field arrives as text; the handler JSON-parses it and only
 * then validates the object against `DocumentMetadataInputSchema`. Declaring
 * the parsed shape would describe something no caller can put on the wire, and
 * would fail every real request the moment anything parsed with it.
 */
export const UploadDocumentFormFieldsSchema = z.object({
  description: z.string().optional(),
  /** JSON object, serialized — validated as `DocumentMetadataInputSchema` after parsing. */
  metadata: z.string().optional()
});
